/* eslint-disable react/prop-types */
import React from 'react';

import { pluralize } from 'utils/string';

import Tooltip from 'components/common/tooltip';

import { MY_TEAM_LABELS, TEAM_COUNT_LABEL, TEAM_COUNT_LABEL_SHORT } from 'constants/myTeam';

import { UiScroll, UiSort } from '@vyaguta/vyaguta-icons/ui';

import AttendanceFilters from './AttendanceFilters';

import type { AttendanceFilter } from '../utils/attendanceFilter';

interface MyTeamHeaderProps {
  myTeamCount: Map<string, Set<number>>;
  currentDayMonthDate: string;
  isAnyAccordionOpen: boolean;
  /**
   * Groups the viewer has in total, ignoring the active category — decides whether
   * the bulk control EXISTS. Kept separate from renderedGroupCount on purpose:
   * these two counted the same prop before, and the ambiguity caused real defects.
   */
  totalGroupCount: number;
  /** Groups actually rendered under the active category — decides whether it is DISABLED. */
  renderedGroupCount: number;
  onToggleAllAccordions: () => void;
  attendanceFilterCounts: Record<AttendanceFilter, number>;
  activeAttendanceFilter: AttendanceFilter | null;
  onAttendanceFilterSelect: (filter: AttendanceFilter) => void;
}

const MyTeamHeader: React.FC<MyTeamHeaderProps> = ({
  myTeamCount,
  currentDayMonthDate,
  isAnyAccordionOpen,
  totalGroupCount,
  renderedGroupCount,
  onToggleAllAccordions,
  attendanceFilterCounts,
  activeAttendanceFilter,
  onAttendanceFilterSelect,
}) => {
  // Existence follows the whole list; a category filter must not make the control
  // blink into being for a viewer who has nothing to bulk-toggle.
  const hasBulkControl = totalGroupCount > 1;
  const hasNothingToToggle = renderedGroupCount === 0;
  // isAnyAccordionOpen is derived across the whole list, not the visible subset,
  // so this label holds as categories change — and an empty category shows that
  // same whole-list label, merely disabled.
  const toggleLabel = isAnyAccordionOpen ? MY_TEAM_LABELS.COLLAPSE_ALL : MY_TEAM_LABELS.EXPAND_ALL;

  // Check if any key in myTeamCount has a non-falsy value in
  // TEAM_COUNT_LABEL_SHORT for the logic to display the divider
  const hasMatchingKey = Array.from(myTeamCount.keys()).some((key) =>
    Boolean(TEAM_COUNT_LABEL_SHORT[key as keyof typeof TEAM_COUNT_LABEL_SHORT])
  );

  return (
    <div className="full-scope-card__header my-team__header-sticky table-header name-wrap">
      <div className="my-team__header d-flex align-items-center justify-content-between f-wrap gap-y-8">
        <div className="my-team__header-heading">
          <div className="table-title-left d-flex my-team__title-left-lock">
            <p className="table-title">{MY_TEAM_LABELS.PAGE_TITLE} </p>
          </div>
          {hasMatchingKey && <span className="my-team__header-divider" />}
          <div className="my-team__header-counts d-flex gap-x-6 cursor-pointer align-items-center justify-content-between">
            {[...myTeamCount].map(([relation, count]) =>
              TEAM_COUNT_LABEL_SHORT[relation] ? (
                <Tooltip
                  key={relation}
                  html={
                    <div className="tooltip-info">
                      <p className="tooltip-info__title">{TEAM_COUNT_LABEL[relation]}</p>
                      <p>
                        {count.size} {pluralize('Leapfrogger', count.size)}
                      </p>
                    </div>
                  }
                >
                  <div className="my-team__header-count">
                    <span>{TEAM_COUNT_LABEL_SHORT[relation]}: </span>
                    <span>{count.size}</span>
                  </div>
                </Tooltip>
              ) : null
            )}
          </div>
        </div>
        <div className="table-title-right r-right d-flex align-items-center justify-content-center">
          <div className="my-team__sub-header d-flex align-items-center">
            <div className="badge__label my-team__header-badge">
              <span className="my-team__header-date">
                <span className="today">{MY_TEAM_LABELS.TODAY} &middot;&nbsp;</span>
                <span> {currentDayMonthDate}</span>
              </span>

              <AttendanceFilters
                counts={attendanceFilterCounts}
                activeFilter={activeAttendanceFilter}
                onSelect={onAttendanceFilterSelect}
              />

              {hasBulkControl && (
                <>
                  <span className="my-team__header-divider" />
                  <button
                    type="button"
                    onClick={onToggleAllAccordions}
                    className="my-team__header-toggle"
                    disabled={hasNothingToToggle}
                    aria-label={toggleLabel}
                    title={toggleLabel}
                  >
                    <span className="my-team__header-toggle-icon">
                      {isAnyAccordionOpen ? <UiScroll /> : <UiSort />}
                    </span>
                    {/* Label is hidden on mobile, where the icon alone carries the control. */}
                    <span className="my-team__header-toggle-label">{toggleLabel}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyTeamHeader;
